import { useState, useCallback, useEffect, useRef } from "react";
import { fetchAllPhotos, fetchPhoto } from "../api";

const PAGE_SIZE = 100;
const hiddenStatuses = new Set(["processing", "pipeline"]);

// Statuses the Overview grid renders when no pill is selected. Passed as
// `defaultStatus` so the backend skips processing-group rows; otherwise
// page-1 of `-created_at` is dominated by in-flight photos and the
// client-side hiddenStatuses filter blanks the grid (and infinite scroll
// is gated on photos.length > 0, so page-2 never loads).
export const VISIBLE_STATUSES = [
  "pending",
  "done",
  "exported",
  "archived",
  "attention",
  "error",
  "export_failed",
  "skipped",
];

const toList = (data) => data?.results || data || [];

const sameList = (a = [], b = []) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    const ai = a[i] || {};
    const bi = b[i] || {};
    if (
      ai.image_id !== bi.image_id ||
      ai.status !== bi.status ||
      ai.attention !== bi.attention ||
      ai.filename !== bi.filename ||
      ai.preview_url !== bi.preview_url ||
      ai.updated_at !== bi.updated_at ||
      ai.has_unlabeled_faces !== bi.has_unlabeled_faces ||
      ai.face_count !== bi.face_count ||
      ai.labeled_count !== bi.labeled_count ||
      ai.unlabeled_count !== bi.unlabeled_count ||
      ai.__cache !== bi.__cache
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Map UI filters to API status parameters.
 *
 * Tank Redesign uses the six-label vocabulary on the StatsPills row
 * (pending / attention / done / exported / error / skipped). Each label
 * is an umbrella for one or more backend statuses; the union here MUST
 * match `getCountForKey` in `StatsPills.jsx` so clicking a tile filters
 * the grid to exactly the photos counted by that tile.
 */
const mapFiltersToApi = (activeFilters) => {
  if (!activeFilters.length) return null;
  const statusFilters = new Set();
  for (const f of activeFilters) {
    switch (f) {
      case "done":
        statusFilters.add("done");
        statusFilters.add("exported");
        statusFilters.add("archived");
        break;
      case "review":
      case "tagging":
        // Both client-side variants of backend `pending`. usePhotoList's
        // post-fetch `filterPhotos` splits on `has_unlabeled_faces`.
        statusFilters.add("pending");
        break;
      case "attention":
      case "problems": // legacy alias
        statusFilters.add("attention");
        statusFilters.add("error");
        statusFilters.add("export_failed");
        break;
      case "skipped":
        statusFilters.add("skipped");
        break;
      case "processing":
        statusFilters.add("processing");
        statusFilters.add("ingested");
        statusFilters.add("recropping");
        statusFilters.add("sent_to_editor");
        break;
      // Legacy direct-status filters (URL hashes from older sessions)
      case "exported":
        statusFilters.add("exported");
        statusFilters.add("archived");
        break;
      case "error":
        statusFilters.add("error");
        statusFilters.add("export_failed");
        break;
      case "recropping":
        statusFilters.add("recropping");
        break;
      case "sent_to_editor":
        statusFilters.add("sent_to_editor");
        break;
      default:
        break;
    }
  }
  return statusFilters.size ? [...statusFilters].join(",") : null;
};

/**
 * Map frontend sort order to backend ordering parameter.
 */
export const mapSortToApi = (sortOrder) => {
  const orderingMap = {
    oldest: "created_at",
    newest: "-created_at",
    filename_asc: "filename",
    filename_desc: "-filename",
    capture_oldest: "capture_date",
    capture_newest: "-capture_date",
  };
  return orderingMap[sortOrder] || "created_at";
};

/**
 * Custom hook for fetching and managing paginated photo lists.
 *
 * @param {object} options - Hook options
 * @param {string[]} options.activeFilters - Active status filters
 * @param {string} options.sortOrder - Sort order (oldest, newest, etc.)
 * @param {string} options.search - Search query
 * @param {number} options.refreshMs - Auto-refresh interval (0 to disable)
 * @param {object} options.imageErrorsRef - Ref to image errors for cache refresh
 * @param {string} options.defaultStatus - Default API status filter when no filters active
 * @param {string} options.includeImageId - Image ID to ensure is in the list (fetched individually if missing)
 * @param {object} options.recentlyActionedRef - Ref to Set of recently actioned image IDs to exclude
 * @param {Function} options.clientFilter - Optional post-fetch client-side filter function
 * @returns {object} - Photo list state and controls
 */
export function usePhotoList({
  activeFilters = [],
  sortOrder = "oldest",
  search = "",
  refreshMs = 10000,
  imageErrorsRef,
  defaultStatus,
  includeImageId,
  recentlyActionedRef,
  clientFilter,
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const photosRef = useRef([]);
  const offsetRef = useRef(0);
  const totalCountRef = useRef(0);

  /**
   * Load photos from API.
   */
  const loadPhotos = useCallback(
    async (opts = {}) => {
      const { quiet, append, refresh, forceImageRefresh } = opts;
      const currentList = photosRef.current || [];

      if (append) {
        setLoadingMore(true);
      } else if (!quiet) {
        setLoading((prev) => (currentList.length ? prev : true));
      }
      setError(null);

      try {
        const filters = {};
        if (search) filters.q = search;

        const statusFilter = mapFiltersToApi(activeFilters);
        if (statusFilter) {
          filters.status = statusFilter;
        } else if (defaultStatus) {
          filters.status = defaultStatus;
        }

        // Tank only edits pipeline photos. Field-ingested photos belong to
        // the phone flow and must not appear in Tank's overview or edit
        // pages. Server supports this filter directly (PhotoViewSet.list).
        filters.ingestion_mode = 'pipeline';

        filters.ordering = mapSortToApi(sortOrder);

        // Pagination: for refresh, fetch all currently loaded; otherwise PAGE_SIZE
        const currentOffset = append ? offsetRef.current : 0;
        const fetchLimit = refresh
          ? Math.max(PAGE_SIZE, offsetRef.current)
          : PAGE_SIZE;

        const data = await fetchAllPhotos(fetchLimit, currentOffset, filters);
        const incoming = toList(data);
        const totalCount = data?.count ?? incoming.length;
        totalCountRef.current = totalCount;

        // Update pagination state (skip for refresh mode)
        if (!refresh) {
          const newOffset = currentOffset + incoming.length;
          offsetRef.current = newOffset;
          setHasMore(newOffset < totalCount && incoming.length === PAGE_SIZE);
        }

        setPhotos((prev) => {
          const prevList = toList(prev);
          const baseList = append ? prevList : [];
          // Precompute Map for O(1) lookup, also reduces nesting depth (S2004)
          const prevMap = new Map(prevList.map((x) => [x.image_id, x]));

          // Process incoming photos
          let processed = incoming.map((p) => {
            // Keep prior visible status if current is hidden
            if (hiddenStatuses.has(p.status)) {
              const prior = prevMap.get(p.image_id);
              if (prior && !hiddenStatuses.has(prior.status)) return prior;
            }
            return p;
          });

          // Handle forced image refresh (cache busting for errors)
          if (forceImageRefresh) {
            processed = processed.map((p) => {
              const prior = prevMap.get(p.image_id);
              const priorCache = prior?.__cache;
              const sameUrls =
                prior?.preview_url === p.preview_url &&
                (prior?.edited_url || prior?.preview_url) ===
                  (p.edited_url || p.preview_url);
              const hadError = imageErrorsRef?.current?.[p.image_id];
              if (hadError) {
                return { ...p, __cache: Date.now() };
              }
              if (priorCache && sameUrls) {
                return { ...p, __cache: priorCache };
              }
              return p;
            });
          }

          // Merge for append mode, avoiding duplicates
          let merged;
          if (append) {
            const existingIds = new Set(baseList.map((p) => p.image_id));
            const newItems = processed.filter((p) => !existingIds.has(p.image_id));
            merged = [...baseList, ...newItems];
          } else {
            merged = processed;
          }

          // Filter out recently actioned photos (zombie prevention)
          if (recentlyActionedRef?.current?.size > 0) {
            merged = merged.filter((p) => !recentlyActionedRef.current.has(p.image_id));
          }

          // Apply optional client-side filter
          if (clientFilter) {
            merged = clientFilter(merged);
          }

          // Avoid unnecessary re-render if same
          if (!append && sameList(prevList, merged)) return prev;

          photosRef.current = merged;
          return merged;
        });

        // If includeImageId is set and not found in incoming results, fetch individually
        if (includeImageId && !append) {
          const inResults = incoming.some((p) => p.image_id === includeImageId);
          if (!inResults) {
            try {
              const photo = await fetchPhoto(includeImageId);
              if (photo) {
                setPhotos((prev) => {
                  const list = toList(prev);
                  if (list.some((p) => p.image_id === includeImageId)) return prev;
                  // Skip if recently actioned (zombie prevention)
                  if (recentlyActionedRef?.current?.has(includeImageId)) return prev;
                  // Skip if client filter would exclude it
                  if (clientFilter && !clientFilter([photo]).length) return prev;
                  const next = [photo, ...list];
                  photosRef.current = next;
                  return next;
                });
              }
            } catch (_e) {
              // Photo may have been deleted; ignore
            }
          }
        }
      } catch (e) {
        console.error("Failed to load photos:", e);
        setError("Failed to load photos");
      } finally {
        if (append) {
          setLoadingMore(false);
        } else if (!quiet) {
          setLoading(false);
        }
      }
    },
    [activeFilters, sortOrder, search, imageErrorsRef, defaultStatus, includeImageId, recentlyActionedRef, clientFilter]
  );

  /**
   * Load more photos for infinite scroll (with cooldown handled by useInfiniteScroll).
   */
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    loadPhotos({ append: true, quiet: true });
  }, [loadPhotos, loadingMore, hasMore]);

  /**
   * Update a single photo in-place (optimistic mutation).
   */
  const updatePhoto = useCallback((imageId, updater) => {
    setPhotos((prev) => {
      const list = toList(prev);
      const next = list.map((p) => {
        if (p.image_id !== imageId) return p;
        return typeof updater === 'function' ? updater(p) : { ...p, ...updater };
      });
      photosRef.current = next;
      return next;
    });
  }, []);

  /**
   * Remove a photo from the list (optimistic removal).
   */
  const removePhoto = useCallback((imageId) => {
    setPhotos((prev) => {
      const list = toList(prev);
      const next = list.filter((p) => p.image_id !== imageId);
      photosRef.current = next;
      return next;
    });
  }, []);

  // Reset pagination when filters/sort/search change
  useEffect(() => {
    offsetRef.current = 0;
    setHasMore(true);
  }, [activeFilters, sortOrder, search]);

  // Load photos when filters/sort/search change (debounced)
  useEffect(() => {
    const t = setTimeout(() => loadPhotos({ quiet: false }), 150);
    return () => clearTimeout(t);
  }, [loadPhotos]);

  // Auto-refresh
  useEffect(() => {
    if (!refreshMs) return undefined;
    const id = setInterval(() => {
      loadPhotos({ quiet: true, forceImageRefresh: true, refresh: true });
    }, refreshMs);
    return () => clearInterval(id);
  }, [loadPhotos, refreshMs]);

  return {
    photos,
    loading,
    loadingMore,
    hasMore,
    error,
    totalCount: totalCountRef.current,
    loadMore,
    refresh: () => loadPhotos({ forceImageRefresh: true }),
    refreshQuiet: () => loadPhotos({ quiet: true, forceImageRefresh: true }),
    updatePhoto,
    removePhoto,
  };
}

/**
 * Client-side filter for photos based on UI filters.
 * Applies additional filtering that can't be done server-side.
 */
export function filterPhotos(photos, activeFilters, timeRange) {
  const list = toList(photos);

  const filterMatches = (p) => {
    if (!activeFilters.length) return true;
    return activeFilters.some((key) => {
      // Tile filters — match `getCountForKey` in StatsPills.
      if (key === "done") return p.status === "done" || p.status === "exported" || p.status === "archived";
      if (key === "review") return p.status === "pending" && !p.has_unlabeled_faces;
      if (key === "tagging") return p.status === "pending" && p.has_unlabeled_faces;
      if (key === "attention" || key === "problems")
        return p.status === "attention" || p.status === "error" || p.status === "export_failed";
      if (key === "skipped") return p.status === "skipped";
      if (key === "processing") {
        return p.status === "processing"
          || p.status === "ingested"
          || p.status === "recropping"
          || p.status === "sent_to_editor";
      }

      // Legacy direct-status filters
      if (key === "exported") return p.status === "exported" || p.status === "archived";
      if (key === "error") return p.status === "error" || p.status === "export_failed";
      if (key === "recropping") return p.status === "recropping";
      if (key === "sent_to_editor") return p.status === "sent_to_editor";
      if (key === "pending") return p.status === "pending" || p.status === "ingested";
      return false;
    });
  };

  const timeMatches = (p) => {
    if (!timeRange) return true;
    // Special filter: show only photos without timestamp
    if (timeRange.noTimestamp) {
      return !p.capture_date;
    }
    if (!p.capture_date) return true; // show photos without capture date
    const t = new Date(p.capture_date).getTime();
    return t >= timeRange.start && t <= timeRange.end;
  };

  return list
    .filter((p) => !hiddenStatuses.has(p.status))
    .filter(filterMatches)
    .filter(timeMatches);
}

/**
 * Compute time bounds from photos with capture dates.
 */
export function computeTimeBounds(photos) {
  const list = toList(photos);
  const timestamps = list
    .filter((p) => p.capture_date)
    .map((p) => new Date(p.capture_date).getTime())
    .filter((t) => Number.isFinite(t));
  if (timestamps.length === 0) return null;
  return {
    min: Math.min(...timestamps),
    max: Math.max(...timestamps),
  };
}

export default usePhotoList;
